import { useEffect } from 'react';
import { NavigationContainer } from '@react-navigation/native';
import { AppRoutes } from './AppRoutes';
import { AdminRoutes } from './AdminRoutes';
import LinkingConfiguration from './LinkingConfiguration';
import { ActivityIndicator } from 'react-native-paper';
import THEME from '../config/theme';
import { StatusBar } from 'expo-status-bar';
import { useUpload } from '../hooks/useUpload';
import {
  CustomThemeProvider,
  useCustomTheme,
} from '../contexts/useCustomTheme';
import { AuthUserProvider, useAuthUser } from '../contexts/useAuthUser';
import Toast from 'react-native-toast-message';
import toastConfig from '../components/Toast/toast.config';
import { ThemeProvider } from 'styled-components/native';
import { RemoteConfigProvider, useRemoteConfigContext } from '../contexts/useRemoteConfigContext';
import { View, ImageBackground } from "react-native"
import { useCustomImages } from '../api/Brand';
import { useCategories } from '../queries/useCategories';
import { StandardText } from '../config/theme/globalStyles';
import LogoLogin from './../../assets/LogoLogin.png';

export default function Navigation({ fontsLoaded }) {
  const config = useRemoteConfigContext();
  const { user, isLoadingUser } = useAuthUser();
  const { updateFavicon } = useUpload();

  const {
    customColor: customTheme,
    isLoadingColorInfo,
    isLoadingFont,
    isLoadingCustomInfo,
  } = useCustomTheme();

  const { data: logo } = useCustomImages('logoAuth');
  const { data: favIcon, isLoading: isLoadingFavIcon } =
    useCustomImages('favIcon');

  // Pré-carrega as imagens de marca que a primeira tela usa (logo do Header e
  // banner da Main). Sem isso cada uma abria o seu próprio loading depois do
  // boot, em sequência. Todas saem do mesmo documento `customization/images`,
  // então o custo aqui é só antecipar leituras que já aconteceriam.
  const { isLoading: isLoadingLogoApp } = useCustomImages('logoApp');
  const { isLoading: isLoadingImageApp } = useCustomImages('imageApp');

  // Consulta mais pesada da tela inicial (categorias + cards de cada uma).
  // Pré-carregada aqui para a lista já vir do cache quando a Main montar, em
  // vez de aparecer depois do banner e do título.
  const { isLoading: isLoadingCategories } = useCategories();

  useEffect(() => {
    if (favIcon?.url) {
      updateFavicon(favIcon.url);
    }
  }, [favIcon?.url]);

  // Loading ÚNICO do app. Tudo que é global (fontes, tema, favicon, imagens de
  // marca, categorias, usuário e remote config) é esperado aqui, de uma vez, e
  // as telas abaixo não devem abrir loading de tela cheia para esses mesmos
  // dados — era isso que empilhava spinners em sequência ao abrir o app.
  // Esperar o remote config também garante que um app indisponível vá direto
  // para a mensagem de bloqueio, sem piscar a tela normal antes.
  const isBooting =
    !fontsLoaded ||
    isLoadingColorInfo ||
    isLoadingFont ||
    isLoadingCustomInfo ||
    isLoadingFavIcon ||
    isLoadingLogoApp ||
    isLoadingImageApp ||
    isLoadingCategories ||
    isLoadingUser ||
    config.isLoadingConfig;

  if (isBooting) {
    return (
      <ActivityIndicator
        style={{
          flex: 1,
          backgroundColor:
            customTheme && customTheme.background && customTheme.background.auth
              ? customTheme.background.auth
              : THEME.COLORS.BACKGROUND_ABOUT,
          justifyContent: 'center',
        }}
        color={
          customTheme && customTheme.primary
            ? customTheme.primary
            : THEME.COLORS.PRIMARY_800
        }
      />
    );
  }

  if (config.isAppUnavailable) {
    return (
      <View
        style={{
          flex: 1,
          backgroundColor:
            customTheme &&
              customTheme.background &&
              customTheme.background.auth
              ? customTheme.background.auth
              : THEME.COLORS.BACKGROUND_ABOUT,
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <ImageBackground
          source={logo ? logo.url : LogoLogin}
          style={{
            height: 180,
            width: 180,
            alignSelf: 'center',
          }}
          resizeMode="contain"
        />
        <StandardText
          color={
            customTheme && customTheme.text && customTheme.text.auth
              ? customTheme.text.auth
              : THEME.COLORS.TEXT_ABOUT
          }
        >
          Aplicativo indisponível no momento. Contate o suporte!
        </StandardText>
      </View>
    );
  }

  return (
    <NavigationContainer linking={LinkingConfiguration}>
      {user?.isAdmin ? (
        <AdminRoutes user={user} />
      ) : (
        <AppRoutes />
      )}
    </NavigationContainer>
  );
}

// Só monta os providers. O loading do app inteiro fica em `Navigation`, que é
// filho deles — antes o gate ficava aqui, fora do `CustomThemeProvider`, então
// `useCustomTheme()` lia o contexto padrão (`{}`): as flags de tema eram sempre
// `undefined` e o spinner nunca usava as cores da marca.
export const MainRoutes = ({ fontsLoaded }) => {
  return (
    <ThemeProvider theme={THEME}>
      <CustomThemeProvider>
        <AuthUserProvider>
          <RemoteConfigProvider>
            <Navigation fontsLoaded={fontsLoaded} />
            <Toast config={toastConfig} />
          </RemoteConfigProvider>
        </AuthUserProvider>
      </CustomThemeProvider>
      <StatusBar style="auto" />
    </ThemeProvider>
  );
};
